import React, { useState, Fragment, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import ConfirmDialog from '../common/ConfirmDialog';
import { 
    UserCircleIcon, 
    ArrowRightOnRectangleIcon,
    HomeIcon,
    AcademicCapIcon,
    NewspaperIcon,
    ArrowLeftOnRectangleIcon,
} from '@heroicons/react/24/outline';
import TranslateIcon from '../icons/TranslateIcon';
import { useHeader } from '../../contexts/HeaderContext';
import { useTranslation } from 'react-i18next';
import '../../styles/Header.css';
import { motion, AnimatePresence } from 'framer-motion';

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

interface HeaderProps {
    isLoggedIn?: boolean;
    userEmail?: string | null;
    onLogin?: () => void;
    onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({
    isLoggedIn,
    userEmail,
    onLogin,
    onLogout
}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, isScrolled, activeLink, activeSubLink, subNavItems } = useHeader();
    const { t, i18n } = useTranslation();
    const headerRef = useRef<HTMLElement>(null);
    const lastScrollY = useRef(0);
    const [isVisible, setIsVisible] = useState(true);

    // 处理滚动隐藏/显示逻辑
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            // 判断是否应该隐藏header (向下滚动且超过一定速度和距离)
            if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
                setIsVisible(false);
            } else {
                setIsVisible(true);
            }
            
            lastScrollY.current = currentScrollY;
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleLoginClick = () => {
        navigate('/login');
    };

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const handleConfirmLogout = async () => {
        try {
            await onLogout?.();
            setShowLogoutConfirm(false);
            navigate('/');
        } catch (error) {
            console.error('退出登录失败:', error);
        }
    };

    const toggleLanguage = () => {
        const newLang = i18n.language === 'zh' ? 'en' : 'zh';
        i18n.changeLanguage(newLang);
    };

    const navigation = [
        { name: `${t('header.nav.home')}`, href: '/', icon: HomeIcon },
        { name: `${t('header.nav.programs')}`, href: '/programs', icon: AcademicCapIcon },
        { name: `${t('header.nav.news')}`, href: '/news', icon: NewspaperIcon }
    ];

    const isActive = (path: string) => {
        if (path === '/') {
            return activeLink === path;
        }
        return activeLink.startsWith(path);
    };

    const getHeaderClass = () => {
        let headerClass = 'header';
        
        if (theme.bgColor === 'bg-transparent' && !isScrolled) {
            headerClass += ' header-transparent';
        }
        
        if (isScrolled) {
            headerClass += ' header-scrolled';
        }
        
        if (!isVisible) {
            headerClass += ' header-hidden';
        }
        
        headerClass = headerClass + ' ' + (isScrolled ? '' : theme.bgColor);
        return headerClass;
    };

    // 判断是否在首页顶部
    // const isHomePageTop = location.pathname === '/' && !isScrolled && theme.bgColor === 'bg-transparent';
    const isHomePageTop = false
    return (
        <>
            <header ref={headerRef} className={getHeaderClass()}>
                <motion.nav 
                    className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex">
                        <Link to="/" className="header-brand">
                            <motion.img 
                                className={`h-12 w-auto header-logo ${isHomePageTop ? 'opacity-0' : ''}`} 
                                src="/logo.png" 
                                alt={t('header.brand.name')}
                                whileHover={{ scale: 1.05, rotate: 2 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            />
                            <div className={`header-brand-text ${theme.textColor} ${isHomePageTop ? 'opacity-0' : ''}`}>
                                <span className="font-bold" style={{ fontFamily: "'KaiTi', 'STKaiti', serif" }}>Great Lakes Academy</span>
                                <span className="font-bold text-sm" style={{ fontFamily: "'KaiTi', 'STKaiti', serif" }}>万湖书院</span>
                            </div>
                        </Link>
                    </div>
                    <div className="flex lg:hidden">
                        <motion.button
                            type="button"
                            className={`inline-flex items-center justify-center rounded-md p-2.5 ${theme.textColor}`}
                            onClick={() => setMobileMenuOpen(true)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                        </motion.button>
                    </div>
                    {/* <div className="hidden lg:flex lg:flex-1 lg:justify-center lg:gap-x-12">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`header-nav-link ${theme.textColor} ${isActive(item.href) ? 'active' : ''}`}
                            >
                                <motion.span 
                                    className="flex items-center gap-2"
                                    whileHover={{ y: -2 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <item.icon className="h-5 w-5 header-nav-icon" aria-hidden="true" />
                                    {item.name}
                                </motion.span>
                            </Link>
                        ))}
                    </div> */}
                    <div className="hidden lg:flex lg:items-center lg:gap-x-6">
                        {isLoggedIn ? (
                            <Menu as="div" className="relative header-dropdown">
                                <Menu.Button className={`flex items-center gap-x-1.5 text-sm font-semibold leading-6 ${isScrolled ? 'text-white hover:text-opacity-90' : theme.textColor} hover:opacity-80`}>
                                    <UserCircleIcon className="h-5 w-5" aria-hidden="true" />
                                    <span>{userEmail}</span>
                                    <motion.div
                                        whileHover={{ rotate: 180 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <ChevronDownIcon className="h-5 w-5 opacity-60" aria-hidden="true" />
                                    </motion.div>
                                </Menu.Button>

                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-200"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-150"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <Menu.Items className="header-dropdown-menu absolute right-0 z-10 mt-2 w-56 origin-top-right">
                                        <div className="py-1">
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <Link
                                                        to="/profile"
                                                        className={`header-dropdown-item flex items-center px-4 py-2 text-sm ${
                                                            active ? 'text-[#142b89]' : 'text-gray-700'
                                                        }`}
                                                    >
                                                        <UserCircleIcon className="mr-3 h-5 w-5" />
                                                        {t('header.userMenu.profile')}
                                                    </Link>
                                                )}
                                            </Menu.Item>
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        onClick={handleLogoutClick}
                                                        className={`header-dropdown-item flex w-full items-center px-4 py-2 text-sm ${
                                                            active ? 'text-red-600' : 'text-gray-700'
                                                        }`}
                                                    >
                                                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                                                        {t('header.userMenu.signOut')}
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        </div>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        ) : (
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Link
                                    to="/login"
                                    className="header-button"
                                >
                                    {t('auth.login')}
                                </Link>
                            </motion.div>
                        )}

                        <div className="h-6 w-px bg-gray-200" />

                        <div className="flex space-x-2">
                            <motion.button
                                onClick={() => i18n.changeLanguage('zh')}
                                className={`header-language-button ${theme.textColor} ${i18n.language === 'zh' ? 'header-nav-link active' : ''}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span>中文</span>
                            </motion.button>
                            <motion.button
                                onClick={() => i18n.changeLanguage('en')}
                                className={`header-language-button ${theme.textColor} ${i18n.language === 'en' ? 'header-nav-link active' : ''}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <span>English</span>
                            </motion.button>
                        </div>
                    </div>
                </motion.nav>

                {/* 副标题导航区域 - 使用i18n翻译 */}
                <div className="sub-header">
                    <div className="mx-auto max-w-7xl px-4 lg:px-8">
                        <ul className="sub-header-nav">
                            {subNavItems.map((item) => (
                                <li key={item.id}>
                                    <Link
                                        to={item.href}
                                        className={`sub-header-link ${activeSubLink === item.id ? 'active' : ''}`}
                                    >
                                        {t(item.name)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
                    <div className="fixed inset-0 z-50" />
                    <Dialog.Panel className="header-mobile-menu fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                        <div className="flex items-center justify-between">
                            <motion.button
                                type="button"
                                className="-m-2.5 rounded-md p-2.5 text-gray-700 hover:bg-gray-100"
                                onClick={() => setMobileMenuOpen(false)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <span className="sr-only">关闭菜单</span>
                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </motion.button>
                        </div>
                        <div className="mt-6 flow-root">
                            <div className="-my-6 divide-y divide-gray-500/10">
                                <div className="space-y-2 py-6">
                                    {/* {navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={`header-nav-link flex items-center gap-2 -mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-white/10 ${isActive(item.href) ? 'bg-white/20' : ''}`}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            <item.icon className="h-5 w-5 header-nav-icon text-white" aria-hidden="true" />
                                            {item.name}
                                        </Link>
                                    ))} */}
                                    
                                    {/* 移动端副标题导航区域 - 使用i18n翻译 */}
                                    <div className="sub-header-mobile">
                                        <div className="sub-header-mobile-title">{t('header.subnav.title')}</div>
                                        {subNavItems.map((item) => (
                                            <Link
                                                key={item.id}
                                                to={item.href}
                                                className={`sub-header-link-mobile ${activeSubLink === item.id ? 'active' : ''}`}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                {t(item.name)}
                                            </Link>
                                        ))}
                                    </div>
                                    
                                    {/* 语言切换选项 */}
                                    <div className="mt-4 border-t border-gray-200/10 pt-4">
                                        <div className="px-3 py-2 text-sm font-medium text-white/60">
                                            {t('header.language.en')} / {t('header.language.zh')}
                                        </div>
                                        <div className="mt-2 space-y-2">
                                            <button
                                                onClick={() => {
                                                    i18n.changeLanguage('zh');
                                                    setMobileMenuOpen(false);
                                                }}
                                                className={`flex w-full items-center gap-2 -mx-3 rounded-lg px-3 py-2 text-base font-semibold leading-7 ${i18n.language === 'zh' ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'}`}
                                            >
                                                <TranslateIcon className="h-5 w-5" />
                                                中文
                                            </button>
                                            <button
                                                onClick={() => {
                                                    i18n.changeLanguage('en');
                                                    setMobileMenuOpen(false);
                                                }}
                                                className={`flex w-full items-center gap-2 -mx-3 rounded-lg px-3 py-2 text-base font-semibold leading-7 ${i18n.language === 'en' ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'}`}
                                            >
                                                <TranslateIcon className="h-5 w-5" />
                                                English
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="py-6">
                                    {isLoggedIn ? (
                                        <>
                                            <div className="px-3 py-2 text-base font-semibold text-white/90">
                                                {userEmail}
                                            </div>
                                            <Link
                                                to="/profile"
                                                className="flex items-center gap-2 -mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-white/10"
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                <UserCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                                                {t('header.userMenu.profile')}
                                            </Link>
                                            <button
                                                onClick={() => {
                                                    setMobileMenuOpen(false);
                                                    handleLogoutClick();
                                                }}
                                                className="flex w-full items-center gap-2 -mx-3 rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-white/10"
                                            >
                                                <ArrowRightOnRectangleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                                                {t('header.userMenu.signOut')}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                setMobileMenuOpen(false);
                                                handleLoginClick();
                                            }}
                                            className="flex w-full items-center gap-2 -mx-3 rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-white/10"
                                        >
                                            <ArrowLeftOnRectangleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                                            {t('auth.login')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Dialog.Panel>
                </Dialog>
            </header>

            <ConfirmDialog
                open={showLogoutConfirm}
                title={t('header.userMenu.signOut')}
                message={t('common.confirmLogout')}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                onConfirm={handleConfirmLogout}
                onCancel={() => setShowLogoutConfirm(false)}
            />
        </>
    );
};

export default Header; 